import { useMemo } from 'react';
import { categoryEmojiMap } from '../data/shared/utils/CategoryEmojiMap';
import { formatSpendCategory } from '../data/shared/utils/formatSpendCategory';
import { Modal } from './design-system/Modal';
import {
  useCategoryEarnRatesAndCreditsV2,
  SelectedCategoriesMultiRate,
} from '../data/shared/hooks/useBestEarnRatesV2';
import { CategoryType } from 'data/shared/__autogenerated__/types/CreditCardCategoryTypes';
import { formatRewardsType } from '../data/shared/utils/formatRewardsType';
import { formatEarnRate } from '../data/shared/utils/formatEarnRate';
import { CreditCard } from 'data/shared/__autogenerated__/types/CreditCard';
import { CreditCardDataFiltered } from 'data/shared/CreditCardData';
import { PromotionalType } from 'data/shared/__autogenerated__/types/PromotionalType';
import { AdvancedCategory, CardCredit } from 'data/types';
import { formatFrequencyText } from '../utils/creditsUtils';
import { TableCardButton } from './TableCardButton';

function categoryStringsMatch(creditCat: string, viewCat: string): boolean {
  if (creditCat === viewCat) {
    return true;
  }
  // Parent→child: credit "travel" matches view "travel.airline"
  if (viewCat.startsWith(`${creditCat}.`)) {
    return true;
  }
  // Child→parent: credit "travel.airline" shows in view "travel"
  if (creditCat.startsWith(`${viewCat}.`)) {
    return true;
  }
  return false;
}

function creditCategoryMatchesViewCategory(
  creditCategory: string | AdvancedCategory | null,
  viewCategory: string
): boolean {
  if (creditCategory == null) {
    return false;
  }

  if (typeof creditCategory === 'object' && 'categories' in creditCategory) {
    return creditCategory.categories.some((cat) =>
      categoryStringsMatch(cat, viewCategory)
    );
  }

  return categoryStringsMatch(creditCategory, viewCategory);
}

function getYearlyMultiplier(frequency: CardCredit['reward_frequency']): number {
  if (!frequency) {
    return 1;
  }
  const normalized = String(frequency).toLowerCase();
  switch (normalized) {
    case 'monthly': return 12;
    case 'quarterly': return 4;
    case 'biannually': return 2;
    default: return 1;
  }
}

export const CategoryModal = ({
  handleClose,
  cards,
  monthlySpend,
  category,
  pointValues,
  selectedCategoriesByCard,
  onOpenCardModal,
  promotionalTypes,
}: {
  category: string;
  cards: CreditCard[];
  monthlySpend: Record<string, number>;
  handleClose: () => void;
  pointValues: Record<string, number>;
  selectedCategoriesByCard: SelectedCategoriesMultiRate;
  onOpenCardModal?: (card: CreditCard) => void;
  promotionalTypes?: PromotionalType[];
}) => {
  // Convert category to CategoryType and get earn rates
  const categoryType = category as CategoryType;

  // Memoize arrays to prevent infinite re-renders
  const categories = useMemo(() => [categoryType], [categoryType]);
  const promoTypesAsStrings = useMemo(
    () => promotionalTypes?.map(String) || [],
    [promotionalTypes]
  );

  const matchingCredits = useMemo(() => {
    const results: {
      card: CreditCard;
      cardData: (typeof CreditCardDataFiltered)[CreditCard];
      credit: CardCredit;
      displayText: string;
      frequencyText: string;
      yearlyValue: number;
    }[] = [];

    for (const card of cards) {
      const data = CreditCardDataFiltered[card];
      if (!data.credits) {
        continue;
      }

      for (const credit of data.credits) {
        if (!creditCategoryMatchesViewCategory(credit.credit_category, categoryType)) {
          continue;
        }

        let displayText: string;
        let frequencyText: string;
        let yearlyValue: number;

        if (credit.total_yearly_value != null) {
          yearlyValue = credit.total_yearly_value;
          displayText = `$${credit.total_yearly_value}`;
          frequencyText = '/year';
        } else if (credit.value != null) {
          const prefix = credit.value_unit !== 'dollars' ? '' : '$';
          displayText = `${prefix}${credit.value}`;
          frequencyText = formatFrequencyText(credit.reward_frequency);
          yearlyValue = credit.value * getYearlyMultiplier(credit.reward_frequency);
        } else {
          continue;
        }

        results.push({ card, cardData: data, credit, displayText, frequencyText, yearlyValue });
      }
    }

    results.sort((a, b) => b.yearlyValue - a.yearlyValue);
    return results;
  }, [cards, categoryType]);

  const categoryEarnRates = useCategoryEarnRatesAndCreditsV2(
    cards,
    categories,
    pointValues,
    true, // Assume in USA for now
    selectedCategoriesByCard,
    promoTypesAsStrings
  );
  const categoryData = categoryEarnRates[categoryType];
  if (!categoryData || categoryData.sortedCardsByEarnRate.length === 0) {
    return null;
  }

  const bestCard = categoryData.sortedCardsByEarnRate[0];
  const rewardsType = bestCard.rewardsType;

  const EARN_RATE_COL_WIDTH = 80;

  const renderCardRow = (
    cardInfo: (typeof categoryData.sortedCardsByEarnRate)[number],
    isBest: boolean,
    isLast: boolean
  ) => {
    const rowCardData = CreditCardDataFiltered[cardInfo.card];
    const cpp = pointValues[cardInfo.rewardsType] ?? 1;
    const effectivePctBack = cardInfo.totalValue;

    return (
      <div
        key={cardInfo.card}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 0',
          borderBottom: !isLast ? '1px solid #f0f0f0' : undefined,
        }}
      >
        <button
          style={{
            width: EARN_RATE_COL_WIDTH,
            flexShrink: 0,
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
            textAlign: 'center',
          }}
          onClick={() => {
            handleClose();
            onOpenCardModal?.(cardInfo.card);
          }}
        >
          <div
            style={{
              fontSize: 18,
              fontWeight: 400,
              fontFamily: 'Merriweather',
              color: '#000',
            }}
          >
            {formatEarnRate(cardInfo.earnRate, cardInfo.rewardsType)}
          </div>
          <div
            style={{
              fontSize: 10,
              lineHeight: 1,
              color: '#5D6C70',
            }}
          >
            {formatRewardsType(cardInfo.rewardsType)}
          </div>
        </button>

        <TableCardButton
          cardData={rowCardData}
          size="small"
          onClick={() => {
            handleClose();
            onOpenCardModal?.(cardInfo.card);
          }}
          style={{
            flexGrow: 0,
            minWidth: 0,
            padding: '4px 8px',
          }}
        />

        <div
          style={{
            flexShrink: 0,
            marginLeft: 'auto',
            textAlign: 'right',
            fontSize: '12px',
            lineHeight: 1.4,
          }}
        >
          <div
            style={{
              fontWeight: isBest ? 600 : 400,
              color: isBest ? '#1a73e8' : '#1a1a1a',
              fontSize: '14px',
            }}
          >
            {effectivePctBack.toFixed(1)}%
          </div>
          <div style={{ color: '#5D6C70' }}>
            {cardInfo.rewardsType === 'cash_back'
              ? 'cash back'
              : `${cpp.toFixed(1)}¢ per pt`}
          </div>
        </div>
      </div>
    );
  };

  const renderCreditRow = (
    { card, cardData: creditCardData, credit, displayText, frequencyText }: (typeof matchingCredits)[number],
    isLast: boolean
  ) => (
    <div
      key={`${card}-${credit.name}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '8px 0',
        borderBottom: !isLast ? '1px solid #f0f0f0' : undefined,
      }}
    >
      <div
        style={{
          width: EARN_RATE_COL_WIDTH,
          flexShrink: 0,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontSize: 18,
            fontWeight: 400,
            fontFamily: 'Merriweather',
            color: '#000',
          }}
        >
          {displayText}
        </div>
        <div
          style={{
            fontSize: 10,
            lineHeight: 1,
            color: '#5D6C70',
          }}
        >
          {frequencyText}
        </div>
      </div>

      <TableCardButton
        cardData={creditCardData}
        size="small"
        onClick={() => {
          handleClose();
          onOpenCardModal?.(card);
        }}
        style={{
          flex: 1,
          minWidth: 0,
          background: 'none',
          padding: '4px 8px',
        }}
      />
    </div>
  );

  const sectionHeaderStyle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'Merriweather, serif',
    color: '#5D6C70',
    margin: 0,
    padding: '16px 0 4px 0',
  };

  const otherCards = categoryData.sortedCardsByEarnRate.slice(1);

  return (
    <Modal handleClose={handleClose} isOpen={true}>
      <h2
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          lineHeight: '1em',
        }}
      >
        {categoryEmojiMap[category]} {formatSpendCategory(category)}
      </h2>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Best card */}
        <p style={sectionHeaderStyle}>Your highest earning card</p>
        {renderCardRow(
          bestCard,
          true,
          otherCards.length === 0 && matchingCredits.length === 0
        )}

        {/* Credits */}
        {matchingCredits.length > 0 && (
          <>
            <p style={sectionHeaderStyle}>Your credits</p>
            {matchingCredits.map((credit, i) =>
              renderCreditRow(credit, i === matchingCredits.length - 1 && otherCards.length === 0)
            )}
          </>
        )}

        {/* Other cards */}
        {otherCards.length > 0 && (
          <>
            <p style={sectionHeaderStyle}>Other cards</p>
            {otherCards.map((cardInfo, index) =>
              renderCardRow(cardInfo, false, index === otherCards.length - 1)
            )}
          </>
        )}
      </div>

      <p style={{ fontSize: 11, color: '#999', marginTop: 16, lineHeight: 1.4 }}>
        {rewardsType !== 'cash_back' && (
          <>
            Based on your point value for {formatRewardsType(rewardsType)} and
            currently selected cards.{' '}
          </>
        )}
        Your &quot;highest earning&quot; card is determined based on point values
        for each of your cards and does not factor in bank credits, promotions,
        or signup bonuses.
      </p>
    </Modal>
  );
};
